import { VariantProps, cva } from 'class-variance-authority';

import { Text } from '@core/components/Text';
import { cn } from '@core/utils/styles';

const avatarStyles = cva(
  [
    'inline-grid shrink-0 align-middle',
    'outline -outline-offset-1 outline-black/20 dark:outline-white/20',
  ],
  {
    variants: {
      rounded: {
        full: 'rounded-full',
        square: 'rounded-theme',
      },
    },
    defaultVariants: {
      rounded: 'full',
    },
  }
);

interface AvatarProps extends VariantProps<typeof avatarStyles> {
  src?: string | null;
  fallbackLetters?: string;
  alt?: string;
  className?: string;
}

/**
 * A versatile Avatar component for displaying user profile images or fallback letters
 *
 * @example
 * ```tsx
 * <Avatar src="/path/to/image.jpg" alt="User Name" />
 * <Avatar fallbackLetters="JD" alt="John Doe" />
 * <Avatar rounded="square" src="/path/to/image.jpg" alt="Square Avatar" />
 * ```
 *
 * @prop {string | null} src - URL of the avatar image. If null or loading fails, falls back to fallback letters
 * @prop {string} rounded - 'full' or 'square' to determine the shape of the avatar
 * @prop {string} fallbackLetters - Text to display when no image is available (typically user's initials)
 * @prop {string} alt - Alt text for accessibility
 * @prop {string} className - Additional CSS classes to apply
 *
 * The component handles various states:
 * - Image loading with fallback to fallbackLetters
 * - Configurable shape (circular or square with rounded corners)
 * - Consistent sizing and styling across light/dark themes
 * - Accessible alt text support
 * - Custom styling via className prop
 */
export function Avatar({
  src = null,
  rounded = 'full',
  fallbackLetters,
  alt = '',
  className,
  ...props
}: AvatarProps) {
  return (
    <span
      data-slot="avatar"
      {...props}
      className={cn(
        avatarStyles({ rounded }),
        fallbackLetters && 'flex items-center justify-center uppercase',
        className
      )}
    >
      {fallbackLetters && (
        <Text aria-hidden={alt ? undefined : 'true'} weight="medium">
          {alt && <title>{alt}</title>}
          {fallbackLetters}
        </Text>
      )}

      {src && <img className="size-full" src={src} alt={alt} />}
    </span>
  );
}
